// =====================================================
// Photo Enlargement Modal - Centered Photo Viewer with Zoom & Pan
// =====================================================

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { useChatStore } from '../store/chatStore';

const PhotoEnlargementModal: React.FC = () => {
  const {
    photoModalOpen,
    photoModalImages,
    photoModalIndex,
    photoModalTitle,
    closePhotoModal,
    setPhotoModalIndex
  } = useChatStore();

  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.5;
  const isZoomed = zoomLevel > 1;

  // Pinch-to-zoom state
  const initialPinchDistance = useRef<number | null>(null);
  const initialPinchZoom = useRef<number>(1);
  const pinchCenter = useRef<{ x: number; y: number } | null>(null);

  // Reset zoom when changing images or closing modal
  useEffect(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, [photoModalIndex, photoModalOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!photoModalOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          if (isZoomed) {
            setZoomLevel(1);
            setPanPosition({ x: 0, y: 0 });
          } else {
            closePhotoModal();
          }
          break;
        case 'ArrowLeft':
          if (!isZoomed) goToPrevious();
          break;
        case 'ArrowRight':
          if (!isZoomed) goToNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [photoModalOpen, photoModalImages.length, isZoomed]);

  const goToPrevious = useCallback(() => {
    setPhotoModalIndex(
      photoModalIndex > 0 ? photoModalIndex - 1 : photoModalImages.length - 1
    );
  }, [photoModalIndex, photoModalImages.length, setPhotoModalIndex]);

  const goToNext = useCallback(() => {
    setPhotoModalIndex(
      photoModalIndex < photoModalImages.length - 1 ? photoModalIndex + 1 : 0
    );
  }, [photoModalIndex, photoModalImages.length, setPhotoModalIndex]);

  // Handle swipe gestures for navigation (only when not zoomed)
  const handleSwipeDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (isZoomed) return;

    const swipeThreshold = 50;
    const velocityThreshold = 500;

    if (info.offset.x > swipeThreshold || info.velocity.x > velocityThreshold) {
      goToPrevious();
    } else if (info.offset.x < -swipeThreshold || info.velocity.x < -velocityThreshold) {
      goToNext();
    }
  };

  // Handle pan drag end when zoomed - update position
  const handlePanDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setPanPosition(prev => ({
      x: prev.x + info.offset.x,
      y: prev.y + info.offset.y
    }));
  };

  // Calculate pan constraints based on zoom level
  const getPanConstraints = () => {
    if (!imageContainerRef.current || !imageRef.current) {
      return { left: -200, right: 200, top: -200, bottom: 200 };
    }

    const containerRect = imageContainerRef.current.getBoundingClientRect();
    const imgRect = imageRef.current.getBoundingClientRect();

    // Calculate how much the image extends beyond the container when zoomed
    const extraWidth = (imgRect.width * zoomLevel - containerRect.width) / 2;
    const extraHeight = (imgRect.height * zoomLevel - containerRect.height) / 2;

    return {
      left: -Math.max(0, extraWidth),
      right: Math.max(0, extraWidth),
      top: -Math.max(0, extraHeight),
      bottom: Math.max(0, extraHeight)
    };
  };

  // Handle double-click/tap to zoom at pointer position
  const handleDoubleClick = (e: React.MouseEvent) => {
    const rect = imageContainerRef.current?.getBoundingClientRect();
    if (!rect) return;

    if (!isZoomed) {
      // Calculate click position relative to center
      const clickX = e.clientX - rect.left - rect.width / 2;
      const clickY = e.clientY - rect.top - rect.height / 2;

      // When zooming in, pan so the clicked point stays under the cursor
      // The pan offset should move the clicked point to center
      const newZoom = 2;
      const panX = -clickX * (newZoom - 1);
      const panY = -clickY * (newZoom - 1);

      setPanPosition({ x: panX, y: panY });
      setZoomLevel(newZoom);
    } else {
      setZoomLevel(1);
      setPanPosition({ x: 0, y: 0 });
    }
  };

  // Handle scroll wheel zoom at pointer position
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();

    const rect = imageContainerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoomLevel + delta));

    if (newZoom !== zoomLevel) {
      if (newZoom === 1) {
        // Reset pan when zooming out completely
        setPanPosition({ x: 0, y: 0 });
      } else {
        // Calculate mouse position relative to center
        const mouseX = e.clientX - rect.left - rect.width / 2;
        const mouseY = e.clientY - rect.top - rect.height / 2;

        // Adjust pan to keep the point under the cursor stationary
        const zoomRatio = newZoom / zoomLevel;
        const newPanX = mouseX - (mouseX - panPosition.x) * zoomRatio;
        const newPanY = mouseY - (mouseY - panPosition.y) * zoomRatio;

        setPanPosition({ x: newPanX, y: newPanY });
      }
      setZoomLevel(newZoom);
    }
  };

  // Calculate distance between two touch points
  const getTouchDistance = (touches: TouchList): number => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Calculate center point between two touches
  const getTouchCenter = (touches: TouchList): { x: number; y: number } => {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  };

  // Handle pinch start
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      initialPinchDistance.current = getTouchDistance(e.touches);
      initialPinchZoom.current = zoomLevel;
      pinchCenter.current = getTouchCenter(e.touches);
    }
  };

  // Handle pinch move
  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && initialPinchDistance.current !== null) {
      e.preventDefault();

      const currentDistance = getTouchDistance(e.touches);
      const scale = currentDistance / initialPinchDistance.current;
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, initialPinchZoom.current * scale));

      if (newZoom !== zoomLevel) {
        const rect = imageContainerRef.current?.getBoundingClientRect();
        if (rect && pinchCenter.current) {
          // Calculate pinch center relative to container center
          const centerX = pinchCenter.current.x - rect.left - rect.width / 2;
          const centerY = pinchCenter.current.y - rect.top - rect.height / 2;

          if (newZoom === 1) {
            setPanPosition({ x: 0, y: 0 });
          } else {
            // Adjust pan to zoom toward pinch center
            const zoomRatio = newZoom / zoomLevel;
            const newPanX = centerX - (centerX - panPosition.x) * zoomRatio;
            const newPanY = centerY - (centerY - panPosition.y) * zoomRatio;
            setPanPosition({ x: newPanX, y: newPanY });
          }
        }
        setZoomLevel(newZoom);
      }
    }
  };

  // Handle pinch end
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      initialPinchDistance.current = null;
      pinchCenter.current = null;
    }
  };

  // Toggle zoom
  const toggleZoom = () => {
    if (isZoomed) {
      setZoomLevel(1);
      setPanPosition({ x: 0, y: 0 });
    } else {
      setZoomLevel(2);
      setPanPosition({ x: 0, y: 0 });
    }
  };

  // Handle click outside to close
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closePhotoModal();
    }
  };

  if (!photoModalOpen || photoModalImages.length === 0) return null;

  const panConstraints = getPanConstraints();

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 z-[100] bg-black/60 flex items-center justify-center p-2"
        onClick={handleBackdropClick}
      >
        {/* Modal Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="relative bg-white rounded-xl shadow-2xl overflow-hidden
                     w-full max-w-[95%] max-h-[95%] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Compact */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            {/* Left: Counter */}
            <span className="text-xs text-gray-400 w-16">
              {photoModalIndex + 1} of {photoModalImages.length}
            </span>
            {/* Center: Title */}
            {photoModalTitle && (
              <span className="text-xs font-semibold text-gray-800 truncate flex-1 text-center">
                {photoModalTitle}
              </span>
            )}
            {/* Right: Buttons */}
            <div className="flex items-center gap-1 w-16 justify-end">
              {/* Zoom Button */}
              <button
                onClick={toggleZoom}
                className={`p-1 rounded-full transition-colors ${
                  isZoomed
                    ? 'bg-betenbough-olive text-white'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                aria-label={isZoomed ? 'Zoom out' : 'Zoom in'}
                title={isZoomed ? 'Zoom out' : 'Zoom in'}
              >
                {isZoomed ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                  </svg>
                )}
              </button>
              {/* Close Button */}
              <button
                onClick={closePhotoModal}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                aria-label="Close photo viewer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Image Container */}
          <div
            ref={imageContainerRef}
            onDoubleClick={handleDoubleClick}
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className={`relative flex-1 bg-gray-100 overflow-hidden flex items-center justify-center ${
              isZoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'
            }`}
          >
            {/* Swipe container for navigation (when not zoomed) */}
            {!isZoomed ? (
              <motion.div
                key={`swipe-${photoModalIndex}`}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={handleSwipeDragEnd}
                className="w-full h-full flex items-center justify-center"
              >
                <img
                  ref={imageRef}
                  src={photoModalImages[photoModalIndex]}
                  alt={`Photo ${photoModalIndex + 1} of ${photoModalImages.length}`}
                  className="max-w-full max-h-full object-contain select-none"
                  draggable={false}
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                  }}
                />
              </motion.div>
            ) : (
              /* Pan container for moving zoomed image */
              <motion.div
                key={`pan-${photoModalIndex}`}
                drag
                dragConstraints={panConstraints}
                dragElastic={0.1}
                dragMomentum={false}
                onDragEnd={handlePanDragEnd}
                initial={false}
                animate={{ x: panPosition.x, y: panPosition.y }}
                className="flex items-center justify-center"
                style={{ touchAction: 'none' }}
              >
                <motion.img
                  ref={imageRef}
                  src={photoModalImages[photoModalIndex]}
                  alt={`Photo ${photoModalIndex + 1} of ${photoModalImages.length}`}
                  className="max-w-full max-h-full object-contain select-none"
                  animate={{ scale: zoomLevel }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  draggable={false}
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                  }}
                />
              </motion.div>
            )}

            {/* Navigation Arrows - Hide when zoomed */}
            {photoModalImages.length > 1 && !isZoomed && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrevious();
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2
                             bg-black/40 hover:bg-black/60 text-white
                             rounded-full p-2 transition-all z-10"
                  aria-label="Previous image"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNext();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2
                             bg-black/40 hover:bg-black/60 text-white
                             rounded-full p-2 transition-all z-10"
                  aria-label="Next image"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* Zoom indicator overlay when zoomed */}
            {isZoomed && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2
                              bg-black/50 text-white text-xs px-3 py-1.5 rounded-full pointer-events-none">
                {Math.round(zoomLevel * 100)}% • Drag to pan
              </div>
            )}
          </div>

          {/* Footer with Dot Indicators and Hints - Hide when zoomed */}
          {!isZoomed && (
            <div className="flex flex-col items-center gap-1 py-2 border-t border-gray-100">
              {/* Dot Indicators */}
              {photoModalImages.length > 1 && (
                <div className="flex justify-center items-center gap-1">
                  {photoModalImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setPhotoModalIndex(index)}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${
                        index === photoModalIndex
                          ? 'bg-betenbough-olive w-3'
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
              {/* Hints */}
              <span className="text-[10px] text-gray-400 md:hidden">
                Swipe to navigate • Pinch or double-tap to zoom
              </span>
              <span className="text-[10px] text-gray-400 hidden md:inline">
                Scroll to zoom • Double-click to enlarge
              </span>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PhotoEnlargementModal;
